import { z } from 'zod';

/**
 * The lesson page is built from a fixed set of blocks. The diagram block names one of the hand-drawn SVGs; nothing
 * about a diagram is generated, because a wrong picture teaches the wrong thing and nobody checks a generated one.
 */
export const ACADEMY_DIAGRAMS = [
  'sources_to_one_shape',
  'three_tiers',
  'tenant_workspace_engagement',
  'bronze_silver_gold',
  'trust_cycle',
  'sdlc_path',
  'request_path',
  'two_rls_paths',
  'claim_row',
  'mapping_rule',
  'engagement_path',
  'deploy_topology',
  // Private markets (the industry track): drawn for the course, nothing of Lantern in them
  'asset_class_map',
  'players_map',
  'fund_structure',
  'waterfall',
  'fund_lifecycle',
  'capital_call_flow',
  'j_curve',
  'valuation_ladder',
  'performance_stack',
  'quarter_calendar',
  'systems_landscape',
] as const;
export type AcademyDiagram = (typeof ACADEMY_DIAGRAMS)[number];

/** `core` first for everyone, then `industry` (private markets, for everyone), then the persona track. */
export const ACADEMY_TRACKS = ['core', 'industry', 'technical', 'cs', 'company'] as const;
export type AcademyTrack = (typeof ACADEMY_TRACKS)[number];

export const ACADEMY_LEVELS = ['spark', 'ember', 'lantern_bearer', 'lantern_master'] as const;
export type AcademyLevel = (typeof ACADEMY_LEVELS)[number];

export const LessonBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('diagram'), diagram: z.enum(ACADEMY_DIAGRAMS), caption: z.string().max(200).optional() }),
  z.object({ type: z.literal('points'), heading: z.string().max(80).default('What to know'), items: z.array(z.string().min(1).max(400)).min(1).max(6) }),
  z.object({
    type: z.literal('example'),
    heading: z.string().max(80).default('Worked example'),
    columns: z.array(z.string().min(1).max(40)).min(2).max(6),
    rows: z.array(z.array(z.string().max(120))).min(1).max(8),
    highlight: z.array(z.number().int().nonnegative()).default([]),
  }),
  z.object({ type: z.literal('compare'), assumed: z.string().min(1).max(400), actual: z.string().min(1).max(600) }),
  z.object({ type: z.literal('try_it'), text: z.string().min(1).max(300), href: z.string().url().or(z.string().startsWith('/')), label: z.string().min(1).max(40) }),
]);
export type LessonBlock = z.infer<typeof LessonBlockSchema>;

export const LessonSchema = z.object({
  title: z.string().min(1).max(120),
  lede: z.string().min(1).max(300),
  blocks: z.array(LessonBlockSchema).min(2).max(6),
  /** Three lines for the Slack DM that announces the lesson. */
  summary: z.array(z.string().min(1).max(200)).min(1).max(3),
  /** What tomorrow is about, for the page footer. Empty before an exam day. */
  next: z.string().max(120).optional(),
});
export type Lesson = z.infer<typeof LessonSchema>;

export const ExamQuestionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('mc'),
    id: z.string().min(1).max(40),
    prompt: z.string().min(1).max(400),
    options: z.array(z.string().min(1).max(200)).min(2).max(4),
    answer: z.number().int().nonnegative(),
    /** The key point this question tests, as its text in the syllabus. */
    key_point: z.string().min(1),
  }),
  z.object({
    kind: z.literal('free_text'),
    id: z.string().min(1).max(40),
    prompt: z.string().min(1).max(600),
    key_point: z.string().min(1),
  }),
]);
export type ExamQuestion = z.infer<typeof ExamQuestionSchema>;

export const ExamSchema = z.object({
  module: z.string().min(1),
  questions: z.array(ExamQuestionSchema).min(1).max(6),
});
export type Exam = z.infer<typeof ExamSchema>;

export const FreeTextGradeSchema = z.object({
  /** 0 to 10. */
  score: z.number().int().min(0).max(10),
  /** The key point was stated or clearly implied. */
  strong: z.boolean(),
  feedback: z.string().min(1).max(600),
});
export type FreeTextGrade = z.infer<typeof FreeTextGradeSchema>;

/** Points: exam score ×10, +5 per consecutive lesson day, +20 for a strong written answer, +25 when feedback is applied. */
export const ACADEMY_POINTS = { exam_multiplier: 10, streak_day: 5, strong_free_text: 20, feedback_applied: 25 } as const;
